import type { Problem } from "./contract.ts";
import { acceptsIdempotencyKey, createIdempotencyKey, IDEMPOTENCY_MISMATCH, isMutation, reportIdempotencyMismatch, type IdempotencyKey } from "./idempotency.ts";
import { ifMatchValue, requiresIfMatch, versionFromEtag } from "./ifMatch.ts";
import { ApiError, OVERLOADED, parseProblem, TRANSPORT_PROBLEM, transportProblem } from "./problem.ts";
import { abortError, delayBeforeRetry, isAbortError, shouldRetry, sleep, type RequestKind } from "./retry.ts";
import { currentTransport } from "./transport.ts";
import { uuidv7 } from "./uuidv7.ts";

/**
 * FR-015-012, `contracts/client-api.md` § Request formation and § Timeouts: one `request()`
 * building the URL under `/api/v1`, sending `credentials: "same-origin"`, forming `Accept`,
 * `Content-Type`, `X-Requested-With: Baton` on every mutation and on a read 001 guards as one
 * because it writes server state (`writesState`), `X-Request-Id` on every
 * request and the optional `Authorization` a token provider supplies, with the 30 second
 * read, 60 second mutation and 5 minute upload timeouts. Retry, idempotency, `If-Match` and
 * problem parsing sit here, above the transport seam, so an installed transport inherits
 * them all. Exactly one implementation of this wrapper exists in the workspace.
 */
export const API_BASE = "/api/v1";

export const TIMEOUTS: Readonly<Record<RequestKind, number>> = { read: 30_000, mutation: 60_000, upload: 300_000, download: 300_000 };

/**
 * The title a request that ran out of its own time (`TIMEOUTS`) is thrown with once its retries are
 * spent, when the host gives no words of its own (`ClientConfig.timeoutWords`). A timeout and a network
 * failure are one failure to the retry and to the offline machine, but not to the person reading it: the
 * server was reached and did not answer in time, where a network failure never reached it (2026-10-13,
 * `contracts/client-api.md` § Timeouts).
 */
export const TIMED_OUT_TITLE = "The server took too long to answer";

/**
 * The headers of a mutation whose body is a file's bytes and which the upload sender sends rather
 * than `request()`, for its progress events (022 FR-022-183, `POST …/accounts/{id}/folder/files`):
 * the content type, the request id and the CSRF header, formed here so this file stays the one
 * place in the workspace that forms them (`contracts/client-api.md` § File uploads).
 */
export function uploadRequestHeaders(contentType: string): Readonly<Record<string, string>> {
  return { "Content-Type": contentType, "X-Request-Id": uuidv7(), "X-Requested-With": "Baton" };
}

/**
 * The headers of a mutation whose answer is an event stream and which the platform's stream
 * source sends rather than `request()`, so the frames reach the stream parser as they arrive
 * (019 `contracts/http-api.md` § Turns, `POST …/threads/{id}/turns` with `Accept: text/event-stream`):
 * the JSON content type, the request id and the CSRF header, formed here for the same reason
 * `uploadRequestHeaders` is.
 */
export function streamRequestHeaders(): Readonly<Record<string, string>> {
  return { "Content-Type": "application/json", "X-Request-Id": uuidv7(), "X-Requested-With": "Baton" };
}

export type QueryValue = string | number | boolean | readonly string[] | null | undefined;

export interface RequestOptions {
  /** A read that writes server state, which 001 guards with `X-Requested-With: Baton` as it guards a mutation (the desktop authorisation request read of 001 FR-009). It carries no idempotency key, because it is not a mutation. */
  readonly writesState?: boolean;
  readonly method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  /** Relative to `/api/v1` unless it begins with `/healthz`, `/readyz`, `/uploads/` or `/downloads/`, which 001 serves outside it. */
  readonly path: string;
  readonly query?: Readonly<Record<string, QueryValue>>;
  readonly body?: unknown;
  /** The key minted when the mutation was created; the wrapper mints one for a mutation that arrives without one. */
  readonly idempotencyKey?: IdempotencyKey;
  /** The cached entity's version for a `PATCH` or `DELETE` of a versioned entity. */
  readonly version?: number;
  /** A `PATCH` or `DELETE` of an entity 001 gives no version (restore, archive, watch) says so explicitly. */
  readonly unversioned?: boolean;
  readonly signal?: AbortSignal;
  readonly kind?: RequestKind;
  readonly headers?: Readonly<Record<string, string>>;
  /** Off for a probe that must answer once (`GET /readyz` from the offline machine). */
  readonly retry?: boolean;
  /** The `Accept` the request sends; `application/json` unless the answer is a file. */
  readonly accept?: string;
  /** How a success answer's body is read: JSON, or the file's bytes as a `Blob` (001 § Export of a task list). A problem is read as JSON either way. */
  readonly response?: "json" | "blob";
  /** Told how much of the body has been sent, of how much, on each attempt (`TransportObserver`). */
  readonly onUploadProgress?: (loaded: number, total: number) => void;
}

export interface ApiResult<T> {
  readonly data: T;
  readonly status: number;
  readonly headers: Headers;
  readonly requestId: string;
  /** The entity version from `ETag`, when the response carried one. */
  readonly version: number | null;
  readonly replayed: boolean;
}

export interface RequestMeta {
  readonly method: string;
  readonly url: string;
  readonly requestId: string;
  readonly kind: RequestKind;
  readonly attempt: number;
  readonly idempotencyKey: IdempotencyKey | null;
}

/** The hooks `@baton/core` installs: the clock offset, the offline machine, the echo
 * suppression set, the permission mirror and the re-authentication flow all read these. */
export interface ClientEvents {
  /** Every request id the client issued, before it is sent, for the event stream's echo guard. */
  readonly onRequest?: (meta: RequestMeta) => void;
  /** Every response, with the server `Date` header for the clock offset. */
  readonly onResponse?: (meta: RequestMeta, response: Response) => void;
  /** A network failure or a timeout, which the offline machine counts. */
  readonly onNetworkFailure?: (meta: RequestMeta, error: unknown) => void;
  /** Every problem the server answered, after retry has given up on it. */
  readonly onProblem?: (meta: RequestMeta, problem: Problem, headers: Headers) => void;
}

export interface ClientConfig {
  readonly baseUrl?: string;
  /**
   * The words a request still refused `overloaded` once its retries are spent is thrown with, in
   * place of the server's title and detail, which name the connection the instance lacked rather
   * than what a person can do. The authenticated entry supplies its catalogue's; a client without
   * them keeps the server's words.
   */
  readonly overloadedWords?: () => string;
  /**
   * The words a request that ran out of its own time is thrown with once its retries are spent, in place
   * of `TIMED_OUT_TITLE`. The authenticated entry supplies its catalogue's; a network failure keeps the
   * transport problem's own title.
   */
  readonly timeoutWords?: () => string;
  /** Agents, scripts and feature 016's background tasks supply a bearer token; the web application never does. */
  readonly tokenProvider?: () => string | null | Promise<string | null>;
  readonly events?: ClientEvents;
  readonly random?: () => number;
}

export interface ApiClient {
  request<T = unknown>(options: RequestOptions): Promise<ApiResult<T>>;
  readonly config: ClientConfig;
}

const OUTSIDE_API = ["/healthz", "/readyz", "/uploads/", "/downloads/"];

function isStringArray(value: unknown): value is readonly string[] {
  return Array.isArray(value);
}

export function buildUrl(baseUrl: string, path: string, query?: Readonly<Record<string, QueryValue>>): string {
  // The generated paths carry `/api/v1` already; a hand-written path is relative to it.
  const relative = path.startsWith(`${API_BASE}/`) ? path.slice(API_BASE.length) : path;
  const prefixed = OUTSIDE_API.some((p) => relative.startsWith(p)) ? relative : `${baseUrl}${relative.startsWith("/") ? relative : `/${relative}`}`;
  const params = new URLSearchParams();
  for (const [name, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null) continue;
    if (isStringArray(value)) for (const v of value) params.append(name, v);
    else params.set(name, String(value));
  }
  const qs = params.toString();
  return qs.length > 0 ? `${prefixed}?${qs}` : prefixed;
}

/** The signal an attempt is sent with, and the timeout's own signal, which says whether the attempt ran out of time. */
function combineSignals(caller: AbortSignal | undefined, timeoutMs: number): { readonly signal: AbortSignal; readonly timeout: AbortSignal } {
  const timeout = AbortSignal.timeout(timeoutMs);
  return { signal: caller ? AbortSignal.any([caller, timeout]) : timeout, timeout };
}

export function createClient(config: ClientConfig = {}): ApiClient {
  const baseUrl = config.baseUrl ?? API_BASE;
  const events = config.events ?? {};

  async function formHeaders(options: RequestOptions, method: string, requestId: string, key: IdempotencyKey | null): Promise<Headers> {
    const headers = new Headers(options.headers);
    headers.set("Accept", options.accept ?? "application/json");
    headers.set("X-Request-Id", requestId);
    if (options.body !== undefined) headers.set("Content-Type", "application/json");
    if (isMutation(method) || options.writesState === true) headers.set("X-Requested-With", "Baton");
    if (isMutation(method) && key !== null) headers.set("Idempotency-Key", key);
    if (requiresIfMatch(method)) {
      if (options.version !== undefined) headers.set("If-Match", ifMatchValue(options.version));
      else if (options.unversioned !== true) {
        throw new Error(`${method} ${options.path} needs the entity version for If-Match, or unversioned: true when 001 gives it none`);
      }
    } else if (options.version !== undefined) {
      // A write another method carries a version on (002's incremental link write, a `POST`
      // under the item's version) sends it the same way (`contracts/client-api.md` § Request formation).
      headers.set("If-Match", ifMatchValue(options.version));
    }
    const token = config.tokenProvider ? await config.tokenProvider() : null;
    if (token !== null && token.length > 0) headers.set("Authorization", `Bearer ${token}`);
    return headers;
  }

  /** A refusal as it is thrown: an `overloaded` one in the host's words when it gave them, with
   * the server's detail dropped so no surface shows the two side by side; any other as answered. */
  function worded(problem: Problem): Problem {
    if (problem.type !== OVERLOADED || config.overloadedWords === undefined) return problem;
    return {
      type: problem.type,
      title: config.overloadedWords(),
      status: problem.status,
      request_id: problem.request_id,
      ...(problem.instance === undefined ? {} : { instance: problem.instance }),
    };
  }

  async function request<T>(options: RequestOptions): Promise<ApiResult<T>> {
    const method = options.method ?? "GET";
    const kind: RequestKind = options.kind ?? (isMutation(method) ? "mutation" : "read");
    const url = buildUrl(baseUrl, options.path, options.query);
    const key: IdempotencyKey | null = isMutation(method) && acceptsIdempotencyKey(options.path) ? (options.idempotencyKey ?? createIdempotencyKey()) : null;
    const retryEnabled = options.retry ?? true;
    const serialisedBody = options.body === undefined ? undefined : JSON.stringify(options.body);

    for (let attempt = 1; ; attempt += 1) {
      if (options.signal?.aborted) throw abortError(options.signal);
      const requestId = uuidv7();
      const meta: RequestMeta = { method, url, requestId, kind, attempt, idempotencyKey: key };
      const headers = await formHeaders(options, method, requestId, key);
      const signals = combineSignals(options.signal, TIMEOUTS[kind]);
      const init: RequestInit = {
        method,
        headers,
        credentials: "same-origin",
        signal: signals.signal,
        ...(serialisedBody === undefined ? {} : { body: serialisedBody }),
      };
      const request = new Request(url, init);
      events.onRequest?.(meta);

      let response: Response;
      try {
        response = await (options.onUploadProgress === undefined ? currentTransport()(request) : currentTransport()(request, { onUploadProgress: options.onUploadProgress }));
      } catch (error) {
        if (options.signal?.aborted || (isAbortError(error) && !init.signal?.aborted)) throw abortError(options.signal);
        events.onNetworkFailure?.(meta, error);
        if (retryEnabled && shouldRetry(kind, 0, attempt)) {
          await sleep(delayBeforeRetry(0, TRANSPORT_PROBLEM, attempt, null, config.random), options.signal);
          continue;
        }
        // The last attempt's own timeout fired: the server took too long, which reads apart from a network failure (2026-10-13).
        throw new ApiError(transportProblem(0, requestId, signals.timeout.aborted ? (config.timeoutWords?.() ?? TIMED_OUT_TITLE) : undefined));
      }
      events.onResponse?.(meta, response);

      if (response.ok) {
        const data = options.response === "blob" ? await response.blob() : response.status === 204 || response.headers.get("Content-Length") === "0" ? undefined : await readJson(response);
        return {
          data: data as T,
          status: response.status,
          headers: response.headers,
          requestId: response.headers.get("X-Request-Id") ?? requestId,
          version: versionFromEtag(response.headers),
          replayed: response.headers.get("Idempotency-Replayed") === "true",
        };
      }

      const answered = await parseProblem(response, requestId);
      const error = new ApiError(answered, response.headers);
      if (answered.type === IDEMPOTENCY_MISMATCH && key !== null) {
        reportIdempotencyMismatch(answered.request_id, key);
        events.onProblem?.(meta, answered, response.headers);
        throw error;
      }
      if (retryEnabled && shouldRetry(kind, response.status, attempt)) {
        await sleep(delayBeforeRetry(response.status, answered.type, attempt, error.retryAfterMs, config.random), options.signal);
        continue;
      }
      const problem = worded(answered);
      events.onProblem?.(meta, problem, response.headers);
      throw problem === answered ? error : new ApiError(problem, response.headers);
    }
  }

  return { request, config };
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) return undefined;
  return JSON.parse(text) as unknown;
}
